<!--
An input that shows the validation rules
-->
<template>
  <div>
    <ValidationProvider
      v-slot="{ errors, valid, handleChange, handleBlur }"
      :vid="group + 'password'"
      :name="($attrs.name as string) || ($attrs.label as string)"
      rules="validPassword"
      :model-value="innerValue"
      @update:model-value="updateValue"
    >
      <b-field
        :type="{
          'is-danger': validationActivated && (errors[0] || customError.length > 0),
          'is-success': valid || !validationActivated,
        }"
        :message="errors + customError"
        class="InputWithValidation-field"
      >
        <template #label>
          <FormInputLabel
            :label="($attrs.label as string) || ''"
            :is-mandatory="true"
            :tooltip="tooltip"
          />
        </template>
        <b-input
          v-model="innerValue"
          :placeholder="placeholder"
          type="password"
          password-reveal
          @update:model-value="
            (val: any) => {
              handleChange && handleChange(val)
              validate(val)
            }
          "
          @blur="(event: any) => handleBlur && handleBlur(event)"
        ></b-input>
      </b-field>
    </ValidationProvider>
    <div class="PasswordCreationWithValidation-hints">
      <div
        :class="{
          'PasswordCreationWithValidation-validationHint': true,
          'is-danger': validationActivated && !atLeastOneLowerCase,
          'is-success': !validationActivated || atLeastOneLowerCase,
        }"
      >
        <b-icon
          size="is-small"
          :icon="icon(!validationActivated || atLeastOneLowerCase)"
          class="mr-2"
        />
        {{ $t("passwordCreation.at-least-one-lowercase-letter") }}
      </div>

      <div
        :class="{
          'PasswordCreationWithValidation-validationHint': true,
          'is-danger': validationActivated && !atLeastOneUpperCase,
          'is-success': !validationActivated || atLeastOneUpperCase,
        }"
      >
        <b-icon
          size="is-small"
          :icon="icon(!validationActivated || atLeastOneUpperCase)"
          class="mr-2"
        />
        {{ $t("passwordCreation.at-least-one-upercase-letter") }}
      </div>

      <div
        :class="{
          'PasswordCreationWithValidation-validationHint': true,
          'is-danger': validationActivated && !atLeastOneDigit,
          'is-success': !validationActivated || atLeastOneDigit,
        }"
      >
        <b-icon
          size="is-small"
          :icon="icon(!validationActivated || atLeastOneDigit)"
          class="mr-2"
        />
        {{ $t("passwordCreation.at-least-one-digit") }}
      </div>

      <div
        :class="{
          'PasswordCreationWithValidation-validationHint': true,
          'is-danger': validationActivated && !correctLength,
          'is-success': !validationActivated || correctLength,
        }"
      >
        <b-icon size="is-small" :icon="icon(!validationActivated || correctLength)" class="mr-2" />
        {{ $t("passwordCreation.at-least-8-chars") }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import { BIcon, BInput, BField } from "buefy"

export default defineComponent({
  name: "PasswordCreationWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BInput,
    BField,
    BIcon,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    modelValue: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      default: "",
    },
    tooltip: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    customError: {
      type: String,
      default: "",
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue", "manual-change"],
  data() {
    return {
      atLeastOneLowerCase: false,
      atLeastOneUpperCase: false,
      atLeastOneDigit: false,
      correctLength: false,
      innerValue: "",
      minimalLength: 8,
      confirmation: "",
    }
  },
  watch: {
    innerValue(newVal: string, _oldValue: string): void {
      this.validate(newVal)
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: string, _oldValue: string): void {
      this.validate(newVal)
      this.innerValue = newVal
      if (newVal != _oldValue) {
        this.$emit("manual-change", newVal)
      }
    },
  },
  created(): void {
    if (this.modelValue) {
      this.innerValue = this.modelValue
      this.validate(this.innerValue)
    }
  },
  methods: {
    validate(value: string): void {
      this.correctLength = value.length >= this.minimalLength
      this.atLeastOneDigit = /\d/.test(value)
      this.atLeastOneLowerCase = /[a-z]/.test(value)
      this.atLeastOneUpperCase = /[A-Z]/.test(value)
    },
    icon(bool: boolean): string {
      if (bool) {
        return "check"
      } else {
        return "times"
      }
    },
    updateValue(newValue: string): void {
      this.innerValue = newValue
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.PasswordCreationWithValidation-hints {
  display: flex;
  align-items: center;
  //justify-content: space-between;
  //margin-bottom: $input-field-margin-bot;
  //margin-top: $input-help-margin-top;
}

.PasswordCreationWithValidation-validationHint {
  display: flex;
  align-items: center;
  //height: 3rem;
  margin-right: 20px;

  &.is-danger {
    color: $danger;
  }

  &.is-success {
    color: $success;
  }
}
</style>
